import gql from '@my/graphql'
import gqlInventory from '@/graphql/inventory'
import gqlStock from '@/graphql/stock'

const tiersMatch = (tierA, tierB) => (
  String(tierA).toLowerCase() === String(tierB).toLowerCase()
)

const findTierGroup = (tierGroups, tier) => (
  tierGroups.find(group => tiersMatch(group.tier, tier))
)

const ensureTierGroup = (tierGroups, tier) => {
  const existingGroup = findTierGroup(tierGroups, tier)

  if (existingGroup) return existingGroup

  const group = { tier, items: [] }
  tierGroups.push(group)

  return group
}

const findTierItem = (group, product, size) => {
  if (!group) return null
  return group.items.find(item => item.product === product && item.size === size)
}

const removeEmptyTierGroup = (tierGroups, tier) => {
  const group = findTierGroup(tierGroups, tier)
  if (group && !group.items.length) {
    const index = tierGroups.findIndex(entry => tiersMatch(entry.tier, tier))
    if (index >= 0) tierGroups.splice(index, 1)
  }
}

export default {
  state: {
    tierGroups: []
  },

  mutations: {
    setTierGroups (state, tierGroups) {
      state.tierGroups = tierGroups
    },

    adjustItemQuantity (state, { tier, product, size, delta, itemTemplate }) {
      if (delta <= 0 && !findTierGroup(state.tierGroups, tier)) return

      const group = delta > 0
        ? ensureTierGroup(state.tierGroups, tier)
        : findTierGroup(state.tierGroups, tier)

      if (!group) return

      const item = findTierItem(group, product, size)

      if (item) {
        item.quantity += delta

        if (item.quantity <= 0) {
          const index = group.items.indexOf(item)
          group.items.splice(index, 1)
          removeEmptyTierGroup(state.tierGroups, tier)
        }

        return
      }

      if (delta <= 0 || !itemTemplate) return

      group.items.push({
        product,
        size,
        quantity: delta,
        brand: itemTemplate.brand,
        folder: itemTemplate.folder,
        subfolder: itemTemplate.subfolder,
        image: itemTemplate.image
      })
    },

    transferItem (state, { sourceTier, targetTier, product, size, quantity, itemTemplate }) {
      const sourceGroup = findTierGroup(state.tierGroups, sourceTier)
      const sourceItem = findTierItem(sourceGroup, product, size)
      if (!sourceItem) return

      sourceItem.quantity -= quantity

      if (sourceItem.quantity <= 0) {
        const index = sourceGroup.items.indexOf(sourceItem)
        sourceGroup.items.splice(index, 1)
        removeEmptyTierGroup(state.tierGroups, sourceTier)
      }

      const targetGroup = ensureTierGroup(state.tierGroups, targetTier)
      const targetItem = findTierItem(targetGroup, product, size)

      if (targetItem) {
        targetItem.quantity += quantity
        return
      }

      targetGroup.items.push({
        product,
        size,
        quantity,
        brand: itemTemplate.brand,
        folder: itemTemplate.folder,
        subfolder: itemTemplate.subfolder,
        image: itemTemplate.image
      })
    },

    addTierGroup (state, { tier }) {
      ensureTierGroup(state.tierGroups, tier)
    }
  },

  actions: {
    async getInventoryProducts ({ commit }, brand) {
      const query = gqlInventory.GET_PRODUCTS

      const response = await gql.query(query, { brand })

      if (!response) return
      if (!response.data || !response.data.getInventoryProducts) return

      commit('setTierGroups', response.data.getInventoryProducts)
    },

    async deleteFromTier ({ commit }, { reference, size, tier, quantity = 1 }) {
      const response = await gql.mutate(gqlStock.DELETE_OF_TIER, {
        reference,
        size,
        tier,
        quantity
      })

      if (!response || !response.data || !response.data.deleteOfTier) return false

      commit('adjustItemQuantity', {
        tier,
        product: reference,
        size,
        delta: -quantity
      })

      return true
    },

    async addToTier ({ commit }, { reference, size, tier, itemTemplate }) {
      const response = await gql.mutate(gqlStock.ADD_TO_TIER, {
        reference,
        tier,
        size
      })

      if (!response || !response.data || !response.data.addToTier) return false

      commit('adjustItemQuantity', {
        tier,
        product: reference,
        size,
        delta: 1,
        itemTemplate
      })

      return true
    },

    async transferBetweenTiers ({ commit }, { reference, size, sourceTier, targetTier, quantity, itemTemplate }) {
      const deleted = await gql.mutate(gqlStock.DELETE_OF_TIER, {
        reference,
        size,
        tier: sourceTier,
        quantity
      })

      if (!deleted || !deleted.data || !deleted.data.deleteOfTier) return false

      for (let index = 0; index < quantity; index++) {
        const added = await gql.mutate(gqlStock.ADD_TO_TIER, {
          reference,
          tier: targetTier,
          size
        })

        if (!added || !added.data || !added.data.addToTier) return false
      }

      commit('transferItem', {
        sourceTier,
        targetTier,
        product: reference,
        size,
        quantity,
        itemTemplate
      })

      return true
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/inventory.js